/**
 * AdminDashboardPage - visão global da plataforma
 * Melhorias: gráficos de crescimento, skeleton loaders, mais KPIs, tickets abertos
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import KPICard from "@/components/shared/KPICard";
import StatusBadge from "@/components/shared/StatusBadge";
import {
    Users, CreditCard, Banknote, LifeBuoy, ArrowUpRight,
    TrendingUp, MessageCircle, Store,
} from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/date";
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

function SkeletonKPI() {
    return (
        <Card className="glass-card p-5 rounded-2xl border-white/10">
            <div className="flex items-start justify-between">
                <div className="flex flex-col gap-2 flex-1">
                    <div className="h-3 w-24 bg-white/10 rounded animate-pulse" />
                    <div className="h-8 w-16 bg-white/10 rounded animate-pulse" />
                </div>
                <div className="h-10 w-10 rounded-xl bg-white/10 animate-pulse" />
            </div>
        </Card>
    );
}

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-card border border-white/10 rounded-xl p-3 text-xs shadow-xl">
            <p className="font-medium mb-1">{label}</p>
            {payload.map((p) => (
                <p key={p.name} style={{ color: p.color }}>
                    {p.name}: {typeof p.value === "number" ? p.value.toLocaleString("pt-PT") : p.value}
                </p>
            ))}
        </div>
    );
};

export default function AdminDashboardPage() {
    const [data, setData] = useState(null);
    const [growth, setGrowth] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/admin/dashboard"),
            api.get("/admin/dashboard/growth").catch(() => ({ data: [] })),
        ]).then(([dash, gr]) => {
            setData(dash.data);
            setGrowth(gr.data || []);
        }).finally(() => setLoading(false));
    }, []);

    return (
        <div className="space-y-6" data-testid="admin-dashboard-page">
            <div>
                <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                    Painel Admin
                </h1>
                <p className="text-muted-foreground">Visão consolidada da plataforma MeSafa.ao</p>
            </div>

            {/* KPI Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {loading ? (
                    Array.from({ length: 4 }).map((_, i) => <SkeletonKPI key={i} />)
                ) : (
                    <>
                        <KPICard icon={Users} label="Total clientes" value={data?.total_clients || 0} accent="#A78BFA" testid="admin-kpi-total-clients" />
                        <KPICard icon={CreditCard} label="Subscrições ativas" value={data?.active_subscriptions || 0} accent="#2DD4BF" testid="admin-kpi-active-subs" />
                        <KPICard
                            icon={Banknote}
                            label="MRR (AOA)"
                            value={(data?.mrr_aoa || 0).toLocaleString("pt-PT")}
                            hint={`${(data?.mrr_usd || 0).toLocaleString("en-US")} USD`}
                            accent="#FBBF24"
                            testid="admin-kpi-mrr"
                        />
                        <KPICard icon={LifeBuoy} label="Pagamentos pendentes" value={data?.pending_payments || 0} accent="#FB7185" testid="admin-kpi-pending" />
                    </>
                )}
            </div>

            {/* Segunda linha de KPIs */}
            {!loading && (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <KPICard icon={MessageCircle} label="Tickets abertos" value={data?.open_tickets || 0} accent="#60A5FA" testid="admin-kpi-open-tickets" />
                    <KPICard icon={TrendingUp} label="Pagamentos aprovados" value={data?.approved_payments || 0} accent="#34D399" testid="admin-kpi-approved-payments" />
                    <KPICard
                        icon={Store}
                        label="Taxa de conversão"
                        value={data?.total_clients > 0 ? `${Math.round((data?.active_subscriptions / data?.total_clients) * 100)}%` : "0%"}
                        accent="#F472B6"
                        testid="admin-kpi-conversion"
                    />
                </div>
            )}

            {/* Gráfico de crescimento */}
            {growth.length > 0 && (
                <Card className="glass-card p-5 rounded-2xl" data-testid="admin-growth-chart">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display font-semibold text-lg">Crescimento (últimos 6 meses)</h3>
                        <Badge variant="outline" className="text-xs border-[#6B21D9]/30 bg-[#6B21D9]/10 text-[#A78BFA]">
                            Clientes & Subscrições
                        </Badge>
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={growth} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorClients" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#A78BFA" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#A78BFA" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="colorSubs" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#2DD4BF" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#2DD4BF" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area type="monotone" dataKey="clients" name="Clientes" stroke="#A78BFA" strokeWidth={2} fill="url(#colorClients)" />
                            <Area type="monotone" dataKey="active_subs" name="Subs. ativas" stroke="#2DD4BF" strokeWidth={2} fill="url(#colorSubs)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>
            )}

            {/* Pagamentos pendentes */}
            <Card className="glass-card p-5 rounded-2xl" data-testid="admin-pending-payments-table">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display font-semibold text-lg">Pagamentos pendentes</h3>
                    <Button asChild variant="ghost" size="sm" className="gap-1">
                        <Link to="/admin/pagamentos">Ver todos <ArrowUpRight className="h-3.5 w-3.5" /></Link>
                    </Button>
                </div>
                {loading ? (
                    <div className="space-y-3">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="h-10 bg-white/5 rounded-lg animate-pulse" />
                        ))}
                    </div>
                ) : (data?.recent_pending || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground py-6 text-center">Sem pagamentos pendentes 🎉</div>
                ) : (
                    <ul className="divide-y divide-white/5">
                        {data.recent_pending.map((p) => (
                            <li key={p.id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                                <div>
                                    <div className="text-sm font-medium">
                                        {p.user_name || "—"} · {p.plan_id}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {fmtDateTime(p.created_at)} ·{" "}
                                        {p.method === "multicaixa_express" ? "Multicaixa Express"
                                            : p.method === "e_kwanza" ? "É-Kwanza"
                                            : p.method === "referencia_mc" ? "Referência MC"
                                            : "Transferência"} · ref. {p.reference}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-semibold">
                                        {p.currency === "USD" ? `${p.amount_usd} USD` : `${(p.amount_aoa || 0).toLocaleString("pt-PT")} AOA`}
                                    </span>
                                    <StatusBadge status={p.status} />
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>
        </div>
    );
}
